import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { legalPages } from "../content/landingContent";
import { useContentField } from "../lib/useContent";
import { useResourceImage } from "../lib/useResourceImage";
import { Footer } from "../components/Footer";
import { WhatsAppFloatButton } from "../components/WhatsAppFloatButton";
import logo from "../assets/brand/logo-header.png";

interface LegalPageProps {
  type: "privacy" | "terms";
}

// Shared shell for /politica-de-privacidad and /terminos-y-condiciones.
// Title + body come from landingContent.json (legalPages.*) via the same
// live-preview hooks the rest of the landing uses, so edits made in
// /content-admin show up here immediately (draft) and permanently after
// "Guardar cambios en el proyecto".
export default function LegalPage({ type }: LegalPageProps) {
  const { src: logoSrc } = useResourceImage("header.logo", logo);
  const titleKey = type === "privacy" ? "legalPages.privacyTitle" : "legalPages.termsTitle";
  const contentKey = type === "privacy" ? "legalPages.privacyContent" : "legalPages.termsContent";
  const titleFallback = type === "privacy" ? legalPages.privacyTitle : legalPages.termsTitle;
  const contentFallback = type === "privacy" ? legalPages.privacyContent : legalPages.termsContent;
  const title = useContentField(titleKey, titleFallback);
  const content = useContentField(contentKey, contentFallback);

  return (
    <div data-testid={`legal-page-${type}`} className="min-h-screen bg-white font-body flex flex-col">
      <header className="border-b border-neutral-200">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 py-5 flex items-center justify-between gap-4">
          <Link to="/" data-testid="legal-page-logo-link" aria-label="MotorPRO Autos — Inicio">
            <img src={logoSrc} alt="MotorPRO Autos" className="h-8 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="legal-page-back-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-neutral-600 hover:text-motor-red transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-3xl w-full px-4 sm:px-6 py-12 sm:py-16">
        <h1
          data-testid="legal-page-title"
          className="font-display font-bold text-3xl sm:text-4xl text-motor-ink leading-tight"
        >
          {title}
        </h1>
        <div
          data-testid="legal-page-content"
          className="mt-8 whitespace-pre-line text-base text-neutral-700 leading-relaxed"
        >
          {content}
        </div>
      </main>

      <Footer />
      <WhatsAppFloatButton />
    </div>
  );
}
